// Achievement system types and interfaces

// Achievement type enum for type safety
export enum AchievementType {
  FIRST_GAME = 'first_game',
  FIRST_MINER = 'first_miner',
  FIRST_RESEARCH = 'first_research',
  FIRST_POWER = 'first_power',
  FIRST_FACTORY = 'first_factory',
  FIRST_BELT = 'first_belt',
  POWER_HUNGRY = 'power_hungry',
  LIGHTS_STAY_ON = 'lights_stay_on',
  FLOW_STATE = 'flow_state',
  DATA_DRIP = 'data_drip',
  EUREKA = 'eureka',
  APPLIED_ENTROPY = 'applied_entropy',
  RESOURCE_BARON = 'resource_baron',
  IRON_REVOLUTION = 'iron_revolution',
  OFFSITE_BACKUP = 'offsite_backup',
}

// Achievement trigger context for checking unlock conditions
export interface AchievementContext {
  // Game state information
  isNewGame?: boolean;
  buildingPlaced?: {
    buildingType: string;
    position: { x: number; y: number };
  };

  researchUnlocked?: {
    researchId: string;
  };

  // Additional context can be added here for future achievements
  resourcesGathered?: {
    type: string;
    amount: number;
  };

  powerDemand?: number;
  powerActiveMachines?: number;
  powerUptimeTicks?: number;
  totalResearchDataProduced?: number;
  totalResearchCompleted?: number;
  totalOreExtracted?: number;
  totalIronPlatesProduced?: number;
  saveExported?: boolean;

  // Meta information
  hasDebugEnabled?: boolean;
}

// Individual achievement interface
export interface AchievementProgress {
  readonly current: number;
  readonly target: number;
}

export interface Achievement {
  readonly id: AchievementType;
  readonly name: string;
  readonly description: string;
  readonly icon: string; // Emoji or character representation
  unlocked: boolean;
  unlockedAt?: number; // Timestamp when unlocked
  progress?: AchievementProgress;
}

// Achievement progress data stored in save files
export interface AchievementSaveData {
  [key: string]: {
    unlocked: boolean;
    unlockedAt?: number;
  };
}

// Achievement system configuration
export interface AchievementSystemConfig {
  // Whether to show achievement notifications
  showNotifications: boolean;

  // Whether achievements are disabled (e.g., when debug mode is active)
  achievementsDisabled: boolean;
}

// Achievement unlock result
export interface AchievementUnlockResult {
  achievement: Achievement;
  isNewlyUnlocked: boolean;
  message?: string;
}
